import { HangoutState } from '../state/HangoutState.js';

export async function buildFrontendStateDiff(conversationHistory, currentState) {
    try {
        // Get scenario baseline data first - use cached data from HangoutState instead of fetching
        let scenarioBaseline = null;
        if (currentState.scenarioId) {
            const hangoutState = HangoutState.getInstance();
            const allScenarios = hangoutState.getScenarios();
            scenarioBaseline = allScenarios.find(s => String(s.id) === String(currentState.scenarioId));
        }

        // Get previous state from last message with stateDiff
        let previousState = null;
        for (let i = conversationHistory.length - 1; i >= 0; i--) {
            const message = conversationHistory[i];
            if (message.stateDiff && Object.keys(message.stateDiff).length > 0) {
                previousState = message.stateDiff;
                break;
            }
        }

        const diff = {};

        // Handle location changes (compare against scenario baseline)
        const rawLocation = currentState.location ?? currentState.loc_id;
        if (rawLocation !== undefined) {
            const baselineLocation = scenarioBaseline?.loc || scenarioBaseline?.loc_id || 0;
            const currentLocation = parseInt(rawLocation);
            const previousLocation = previousState
                ? (previousState.location ?? previousState.loc_id)
                : undefined;

            // Include in diff if different from baseline AND changed from previous state
            if (currentLocation !== baselineLocation &&
                previousLocation !== currentLocation) {
                diff.location = currentLocation;
            }
        }

        // Handle time changes (compare against scenario baseline)
        if (currentState.currentTime !== undefined) {
            // Convert scenario timeOfDay to comparable format if needed
            let baselineTime = null;
            if (scenarioBaseline?.timeOfDay) {
                // Convert timeOfDay to hour format for comparison
                switch (scenarioBaseline.timeOfDay) {
                    case 'day': baselineTime = '14:00'; break;
                    case 'sunset': baselineTime = '18:00'; break;
                    case 'night': baselineTime = '21:00'; break;
                    default: baselineTime = '20:00';
                }
            }

            // Include in diff if different from baseline AND changed from previous state
            if (baselineTime && currentState.currentTime !== baselineTime &&
                (!previousState || previousState.current_time !== currentState.currentTime)) {
                diff.current_time = currentState.currentTime;
            } else if (!baselineTime &&
                (!previousState || previousState.current_time !== currentState.currentTime)) {
                // No baseline available, include if changed from previous
                diff.current_time = currentState.currentTime;
            }
        }

        // Handle scenario_id changes (only when changed from previous state)
        if (currentState.scenarioId !== undefined &&
            (!previousState || previousState.scenario_id !== parseInt(currentState.scenarioId))) {
            diff.scenario_id = parseInt(currentState.scenarioId);
        }

        // Handle scenarioDescription changes (compare against scenario baseline)
        if (typeof currentState.scenarioDescription === 'string') {
            const normalizedScenarioDescription = currentState.scenarioDescription.trim();
            if (normalizedScenarioDescription !== '') {
                const baselineDescription = scenarioBaseline?.descr || scenarioBaseline?.description;

                // Only include scenarioDescription if it's different from baseline AND changed from previous state
                if (baselineDescription) {
                    if (normalizedScenarioDescription !== baselineDescription &&
                        (!previousState || previousState.scenarioDescription !== normalizedScenarioDescription)) {
                        diff.scenarioDescription = normalizedScenarioDescription;
                    }
                } else if (!previousState || previousState.scenarioDescription !== normalizedScenarioDescription) {
                    // If baseline description is missing, include if changed from previous
                    diff.scenarioDescription = normalizedScenarioDescription;
                }
            }
        }

        // Handle character changes (compare against scenario baseline)
        if (currentState.characters && Array.isArray(currentState.characters)) {
            const characterChanges = [];

            currentState.characters.forEach((char, index) => {
                const charDiff = { id: char.id };
                let hasChanges = false;

                // Check outfit changes
                if (char.outfitId !== undefined) {
                    const previousChar = previousState?.characters?.find(c => c.id === char.id);
                    const newOutfitId = parseInt(char.outfitId);
                    if (!previousChar || previousChar.outfitId !== newOutfitId) {
                        charDiff.outfitId = newOutfitId;
                        hasChanges = true;
                    }
                }

                // Include voice_id and voice_language when present and changed
                if (char.voice_id) {
                    const previousChar = previousState?.characters?.find(c => c.id === char.id);
                    if (!previousChar || previousChar.voice_id !== char.voice_id) {
                        charDiff.voice_id = char.voice_id;
                        hasChanges = true;
                    }
                }
                if (char.voice_language) {
                    const previousChar = previousState?.characters?.find(c => c.id === char.id);
                    if (!previousChar || previousChar.voice_language !== char.voice_language) {
                        charDiff.voice_language = char.voice_language;
                        hasChanges = true;
                    }
                }

                // Check goal changes (compare against scenario baseline using character index)
                if (char.goal !== undefined) {
                    const previousChar = previousState?.characters?.find(c => c.id === char.id);
                    const previousGoal = typeof previousChar?.goal === 'string'
                        ? previousChar.goal.trim()
                        : previousChar?.goal;

                    // Get baseline goal for this character from scenario-level definitions.
                    let baselineGoal = null;
                    if (scenarioBaseline) {
                        const goalKey = `goal_${index + 1}`;
                        const slotGoal = scenarioBaseline[goalKey];
                        if (typeof slotGoal === 'string' && slotGoal.trim() !== '') {
                            baselineGoal = slotGoal.trim();
                        }

                        if (!baselineGoal && Array.isArray(scenarioBaseline.chars)) {
                            const byId = scenarioBaseline.chars.find(sChar => String(sChar?.id) === String(char.id));
                            if (typeof byId?.goal === 'string' && byId.goal.trim() !== '') {
                                baselineGoal = byId.goal.trim();
                            } else {
                                const byIndexGoal = scenarioBaseline.chars[index]?.goal;
                                if (typeof byIndexGoal === 'string' && byIndexGoal.trim() !== '') {
                                    baselineGoal = byIndexGoal.trim();
                                }
                            }
                        }

                        if (!baselineGoal && index === 0 && typeof scenarioBaseline.goal === 'string' && scenarioBaseline.goal.trim() !== '') {
                            baselineGoal = scenarioBaseline.goal.trim();
                        }
                    }

                    // Only include goal if it's different from baseline AND changed from previous state
                    const normalizedGoal = typeof char.goal === 'string' ? char.goal.trim() : char.goal;
                    if (baselineGoal && normalizedGoal !== baselineGoal && normalizedGoal !== previousGoal) {
                        charDiff.goal = normalizedGoal;
                        hasChanges = true;
                    } else if (!baselineGoal && normalizedGoal !== previousGoal) {
                        // No baseline goal available, include if changed from previous
                        charDiff.goal = normalizedGoal;
                        hasChanges = true;
                    }
                }

                if (hasChanges) {
                    characterChanges.push(charDiff);
                }
            });

            if (characterChanges.length > 0) {
                diff.characters = characterChanges;
            }
        }
        return Object.keys(diff).length > 0 ? diff : null;
    } catch (error) {
        console.error('buildFrontendStateDiff: Error building state diff:', error);
        return null;
    }
}
